import type { Client } from '../api/client.js';
import type { Session } from '../api/contrat.js';
import { ESPACES, libelleCourtRole, roleEffectif } from './roles.js';
import { COULEURS, styleBouton } from './styles.js';

/**
 * Bandeau permanent indigo « Voir en tant que » (UX-16, CA-ADM-01a) : rappelle qui est incarné, si la vue est en
 * lecture seule, que la session est tracée, et permet de revenir à sa propre vue.
 */
export function libelleBandeau(session: Session): string | null {
  const { impersonation } = session;
  if (!impersonation) return null;
  // Seule l'équipe MAAT incarne. Une charge d'incarnation reçue par un client ne doit jamais nommer autrui à l'écran.
  const reel = session.user.roles[0]?.role;
  if (!reel || ESPACES[reel].espace !== 'equipe') return null;
  const role = libelleCourtRole(roleEffectif(session));
  const mode = impersonation.mode === 'read' ? 'lecture seule · session tracée' : 'actions tracées · session tracée';
  return `Vous voyez Lakou en tant que ${impersonation.targetDisplayName} (${role}) · ${mode} · Revenir à ma vue`;
}

export function BandeauVoirEnTantQue({ session, onRevenir }: { session: Session; onRevenir: () => void }) {
  const libelle = libelleBandeau(session);
  if (!libelle) return null;
  const phrase = libelle.replace(/ · Revenir à ma vue$/, '');
  return (
    <div
      data-bandeau="voir-en-tant-que"
      role="region"
      aria-label="Voir en tant que"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '8px 16px',
        background: COULEURS.indigo,
        color: COULEURS.blanc,
        fontSize: 16,
        lineHeight: 1.5,
      }}
    >
      <span>{phrase}</span>
      <button type="button" onClick={onRevenir} style={{ ...styleBouton, background: COULEURS.blanc, color: COULEURS.indigo }}>
        Revenir à ma vue
      </button>
    </div>
  );
}

/** Termine la session d’incarnation et rend à la personne sa propre vue. */
export async function revenirAMaVue(client: Client, session: Session): Promise<void> {
  if (!session.impersonation) return;
  await client.appeler('impersonation.terminer', { parametres: { sessionId: session.impersonation.id } });
}
